import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  FolderPlus, Upload, Trash2, RotateCcw, Folder, FileText, FileSpreadsheet, FileImage, FileArchive,
  Home, ChevronRight, Lock, Unlock, Download, Pencil, Eye, RefreshCw, History, AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const iconForMime = (mime, name) => {
  const n = (name || "").toLowerCase();
  if (n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".csv") || (mime && mime.includes("sheet"))) return FileSpreadsheet;
  if (n.match(/\.(png|jpe?g|gif|webp|svg|heic)$/) || (mime && mime.startsWith("image/"))) return FileImage;
  if (n.match(/\.(zip|rar|7z|tar|gz)$/) || (mime && mime.includes("zip"))) return FileArchive;
  return FileText;
};

const formatSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const timeAgo = (secs) => {
  if (secs == null) return "";
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

export default function DocumentiCondivisiPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [parentId, setParentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeHistory, setIncludeHistory] = useState(false);

  const [showTrash, setShowTrash] = useState(false);
  const [trashItems, setTrashItems] = useState([]);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  // Viewer / editor modal state
  const [viewer, setViewer] = useState(null); // {doc, mode:"view"|"edit", lock, url, mime}
  const heartbeatRef = useRef(null);
  const replaceInputRef = useRef(null);
  const [replacing, setReplacing] = useState(false);

  const load = async (pid = parentId) => {
    setLoading(true);
    try {
      const { data } = await api.get("/shared-docs/list", {
        params: { parent_id: pid || undefined, include_history: includeHistory },
      });
      setItems(data.items);
      setBreadcrumb(data.breadcrumb);
      setParentId(pid);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadTrash = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shared-docs/trash");
      setTrashItems(data.items);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (showTrash) loadTrash();
    else load(parentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrash, includeHistory]);

  // Auto-refresh every 15s to see who's editing what
  useEffect(() => {
    if (showTrash) return;
    const id = setInterval(() => load(parentId), 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, showTrash, includeHistory]);

  const openFolder = (folder) => load(folder.id);
  const goRoot = () => load(null);
  const goBreadcrumb = (idx) => {
    if (idx < 0) return goRoot();
    load(breadcrumb[idx].id);
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await api.post("/shared-docs/folder", { name, parent_id: parentId });
      toast.success(t("shared_docs.folder_created"));
      setNewFolderOpen(false);
      setNewFolderName("");
      load(parentId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadFile = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        if (parentId) fd.append("parent_id", parentId);
        await api.post("/shared-docs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(t("shared_docs.upload_done", { count: fileList.length }));
      load(parentId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const deleteItem = async (doc) => {
    try {
      await api.delete(`/shared-docs/${doc.id}`);
      toast.success(t("shared_docs.moved_to_trash"));
      load(parentId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const restoreItem = async (doc) => {
    try {
      await api.post(`/shared-docs/${doc.id}/restore`);
      toast.success(t("shared_docs.restored"));
      loadTrash();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const purgeItem = async (doc) => {
    if (!window.confirm(t("shared_docs.confirm_purge", { name: doc.name }))) return;
    try {
      await api.delete(`/shared-docs/trash/${doc.id}`);
      toast.success(t("shared_docs.purged"));
      loadTrash();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitRename = async () => {
    const name = renameValue.trim();
    if (!name || !renameTarget) return;
    try {
      await api.post(`/shared-docs/${renameTarget.id}/rename`, { name });
      toast.success(t("shared_docs.renamed"));
      setRenameOpen(false);
      setRenameTarget(null);
      load(parentId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---------- Viewer / Lock lifecycle ----------
  const buildDownloadUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/shared-docs/${id}/download`;

  const openViewer = async (doc, mode) => {
    let lock = null;
    if (mode === "edit") {
      try {
        const { data } = await api.post(`/shared-docs/${doc.id}/lock`);
        lock = data.lock;
      } catch (e) {
        if (e.response?.status === 423) {
          const other = e.response.data?.detail?.lock;
          toast.error(t("shared_docs.locked_by", { user: other?.user_name || other?.user_email || "?" }));
          // Fall back to read-only
          mode = "view";
          lock = other;
        } else {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
          return;
        }
      }
    } else {
      // Just check current lock so UI shows warning
      try {
        const { data } = await api.get(`/shared-docs/${doc.id}/lock`);
        lock = data.lock;
      } catch { /* ignore */ }
    }
    setViewer({ doc, mode, lock });
  };

  const closeViewer = async () => {
    const v = viewer;
    setViewer(null);
    if (v?.mode === "edit" && v.lock?.is_self) {
      try { await api.post(`/shared-docs/${v.doc.id}/unlock`); } catch { /* ignore */ }
    }
  };

  // Heartbeat while editing
  useEffect(() => {
    if (viewer?.mode === "edit") {
      heartbeatRef.current = setInterval(async () => {
        try {
          const { data } = await api.post(`/shared-docs/${viewer.doc.id}/lock`);
          setViewer((v) => v ? { ...v, lock: data.lock } : v);
        } catch { /* keep going; will error on save */ }
      }, 30000);
      return () => clearInterval(heartbeatRef.current);
    }
  }, [viewer?.mode, viewer?.doc?.id]);

  // Release lock on tab close
  useEffect(() => {
    const onUnload = () => {
      if (viewer?.mode === "edit" && viewer.lock?.is_self) {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/shared-docs/${viewer.doc.id}/unlock`;
        try {
          const token = localStorage.getItem("access_token");
          fetch(url, { method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, keepalive: true });
        } catch { /* ignore */ }
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [viewer]);

  const uploadNewVersion = async (files) => {
    if (!files || !files[0] || !viewer) return;
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      await api.put(`/shared-docs/${viewer.doc.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("shared_docs.version_uploaded"));
      // Release lock and close
      try { await api.post(`/shared-docs/${viewer.doc.id}/unlock`); } catch { /* ignore */ }
      setViewer(null);
      load(parentId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReplacing(false); if (replaceInputRef.current) replaceInputRef.current.value = ""; }
  };

  const downloadDoc = async (doc) => {
    try {
      const { data } = await api.get(`/shared-docs/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---------- Render ----------
  return (
    <div className="space-y-5" data-testid="shared-docs-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("shared_docs.kicker")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.shared_docs")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("shared_docs.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={showTrash ? "default" : "outline"} onClick={() => setShowTrash(!showTrash)} className="rounded-sm" data-testid="btn-trash-toggle">
            <Trash2 className="mr-2 h-4 w-4" /> {t("shared_docs.trash_btn")}
          </Button>
          {!showTrash && (
            <>
              <Button variant="outline" onClick={() => setNewFolderOpen(true)} className="rounded-sm" data-testid="btn-new-folder">
                <FolderPlus className="mr-2 h-4 w-4" /> {t("shared_docs.new_folder")}
              </Button>
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-upload">
                <Upload className="mr-2 h-4 w-4" /> {uploading ? t("shared_docs.uploading") : t("shared_docs.upload_btn")}
              </Button>
              <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => uploadFile(e.target.files)} data-testid="file-input" />
            </>
          )}
        </div>
      </div>

      {/* Breadcrumb */}
      {!showTrash && (
        <div className="flex flex-wrap items-center gap-1 text-sm">
          <button onClick={goRoot} className="flex items-center gap-1 rounded-sm px-2 py-1 hover:bg-slate-100" data-testid="crumb-root">
            <Home className="h-3.5 w-3.5" /> {t("shared_docs.root")}
          </button>
          {breadcrumb.map((b, i) => (
            <React.Fragment key={b.id}>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
              <button onClick={() => goBreadcrumb(i)} className="rounded-sm px-2 py-1 font-medium hover:bg-slate-100">{b.name}</button>
            </React.Fragment>
          ))}
          <label className="ml-4 flex items-center gap-1 text-xs text-slate-500 cursor-pointer">
            <input type="checkbox" checked={includeHistory} onChange={(e) => setIncludeHistory(e.target.checked)} data-testid="chk-include-history" />
            {t("shared_docs.show_history_folders")}
          </label>
        </div>
      )}

      {/* Items grid */}
      <div className="rounded-sm border border-slate-200 bg-white">
        {loading && (
          <div className="p-8 text-center text-sm text-slate-400"><RefreshCw className="mx-auto mb-2 h-4 w-4 animate-spin" /> {t("common.loading")}</div>
        )}
        {!loading && !showTrash && items.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-400">
            <Folder className="mx-auto mb-3 h-10 w-10 opacity-30" />
            {t("shared_docs.empty_hint")}
          </div>
        )}
        {!loading && showTrash && trashItems.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-400">
            <Trash2 className="mx-auto mb-3 h-10 w-10 opacity-30" />
            {t("shared_docs.trash_empty")}
          </div>
        )}

        {!loading && !showTrash && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">{t("shared_docs.col_name")}</th>
                <th className="p-3 text-left">{t("shared_docs.col_status")}</th>
                <th className="p-3 text-left">{t("shared_docs.col_owner")}</th>
                <th className="p-3 text-right">{t("shared_docs.col_size")}</th>
                <th className="p-3 text-left">{t("shared_docs.col_updated")}</th>
                <th className="p-3 text-right">{t("shared_docs.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const Icon = it.is_folder ? Folder : iconForMime(it.mime_type, it.name);
                const lockedByOther = !it.is_folder && it.lock && !it.lock.is_self;
                const lockedBySelf = !it.is_folder && it.lock && it.lock.is_self;
                return (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-${it.id}`}>
                    <td className="p-3">
                      <button
                        onClick={() => it.is_folder ? openFolder(it) : openViewer(it, "view")}
                        className="flex items-center gap-2 text-left font-medium text-slate-900 hover:text-[#0052FF]"
                        data-testid={`open-${it.id}`}
                      >
                        <Icon className={`h-5 w-5 flex-shrink-0 ${it.is_folder ? "text-[#0052FF]" : "text-slate-500"}`} />
                        <span className="truncate">{it.name}</span>
                        {it.is_history && <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">{t("shared_docs.history_badge")}</span>}
                      </button>
                    </td>
                    <td className="p-3">
                      {lockedByOther && (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">
                          <Lock className="h-3 w-3" /> {t("shared_docs.in_use_by", { user: it.lock.user_name, ago: timeAgo(it.lock.seconds_since_ping) })}
                        </span>
                      )}
                      {lockedBySelf && (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                          <Pencil className="h-3 w-3" /> {t("shared_docs.edit_by_you")}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-slate-500">{it.created_by_name || "—"}</td>
                    <td className="p-3 text-right font-mono text-xs text-slate-500">{it.is_folder ? "—" : formatSize(it.size)}</td>
                    <td className="p-3 text-xs text-slate-500">{new Date(it.updated_at).toLocaleString()}</td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        {!it.is_folder && (
                          <Button size="sm" variant="ghost" className="h-7 rounded-sm px-2" onClick={() => downloadDoc(it)} title={t("common.download")} data-testid={`download-${it.id}`}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {!it.is_history && (
                          <Button size="sm" variant="ghost" className="h-7 rounded-sm px-2" onClick={() => { setRenameTarget(it); setRenameValue(it.name); setRenameOpen(true); }} title={t("common.rename")}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 rounded-sm px-2 text-red-600 hover:bg-red-50" title={t("common.delete")}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("shared_docs.confirm_delete_title", { name: it.name })}</AlertDialogTitle>
                              <AlertDialogDescription>{t("shared_docs.confirm_delete_hint")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteItem(it)}>{t("common.delete")}</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && showTrash && trashItems.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">{t("shared_docs.col_name")}</th>
                <th className="p-3 text-left">{t("shared_docs.deleted_at")}</th>
                <th className="p-3 text-right">{t("shared_docs.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {trashItems.map((it) => {
                const Icon = it.is_folder ? Folder : iconForMime(it.mime_type, it.name);
                return (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-5 w-5 ${it.is_folder ? "text-slate-400" : "text-slate-400"}`} />
                        <span className="truncate line-through text-slate-500">{it.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-xs text-slate-500">{it.deleted_at ? new Date(it.deleted_at).toLocaleString() : "—"}</td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => restoreItem(it)}>
                          <RotateCcw className="mr-1 h-3 w-3" /> {t("shared_docs.restore")}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 rounded-sm text-xs text-red-600 hover:bg-red-50" onClick={() => purgeItem(it)}>
                          <Trash2 className="mr-1 h-3 w-3" /> {t("shared_docs.purge")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("shared_docs.new_folder")}</DialogTitle></DialogHeader>
          <div>
            <Label>{t("shared_docs.folder_name")}</Label>
            <Input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} className="rounded-sm" data-testid="folder-name-input" onKeyDown={(e) => e.key === "Enter" && createFolder()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={createFolder} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="folder-create-btn">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("shared_docs.rename_title")}</DialogTitle></DialogHeader>
          <Input autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="rounded-sm" onKeyDown={(e) => e.key === "Enter" && submitRename()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={submitRename} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File viewer / editor */}
      {viewer && (
        <Dialog open onOpenChange={(o) => !o && closeViewer()}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {viewer.mode === "edit" ? <Pencil className="h-4 w-4 text-emerald-600" /> : <Eye className="h-4 w-4 text-slate-500" />}
                <span className="truncate">{viewer.doc.name}</span>
              </DialogTitle>
            </DialogHeader>

            {/* Lock banner */}
            {viewer.lock && !viewer.lock.is_self && (
              <div className="flex items-start gap-2 rounded-sm border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <div className="font-semibold">{t("shared_docs.locked_banner_title")}</div>
                  <div className="text-xs">{t("shared_docs.locked_banner_hint", { user: viewer.lock.user_name || viewer.lock.user_email, ago: timeAgo(viewer.lock.seconds_since_ping) })}</div>
                </div>
              </div>
            )}
            {viewer.mode === "edit" && viewer.lock?.is_self && (
              <div className="flex items-center gap-2 rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                <Lock className="h-4 w-4" />
                <div>
                  <div className="font-semibold">{t("shared_docs.edit_banner_title")}</div>
                  <div className="text-xs">{t("shared_docs.edit_banner_hint")}</div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3">
              <Button variant="outline" onClick={() => downloadDoc(viewer.doc)} className="rounded-sm">
                <Download className="mr-2 h-4 w-4" /> {t("shared_docs.download_current")}
              </Button>
              {viewer.mode === "view" && (!viewer.lock || viewer.lock.is_self) && (
                <Button onClick={() => openViewer(viewer.doc, "edit")} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="viewer-take-edit">
                  <Pencil className="mr-2 h-4 w-4" /> {t("shared_docs.take_edit_lock")}
                </Button>
              )}
              {viewer.mode === "edit" && (
                <>
                  <Button onClick={() => replaceInputRef.current?.click()} disabled={replacing} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="viewer-upload-version">
                    <Upload className="mr-2 h-4 w-4" /> {replacing ? t("shared_docs.uploading") : t("shared_docs.upload_new_version")}
                  </Button>
                  <input ref={replaceInputRef} type="file" hidden onChange={(e) => uploadNewVersion(e.target.files)} />
                  <Button variant="outline" onClick={closeViewer} className="rounded-sm">
                    <Unlock className="mr-2 h-4 w-4" /> {t("shared_docs.release_lock")}
                  </Button>
                </>
              )}
            </div>

            <div className="text-[11px] text-slate-500">
              <div>{t("shared_docs.updated_by")} <b>{viewer.doc.created_by_name || "—"}</b></div>
              <div>{t("shared_docs.updated_at_label")} {new Date(viewer.doc.updated_at).toLocaleString()}</div>
              <div className="mt-1 flex items-center gap-1"><History className="h-3 w-3" /> {t("shared_docs.history_hint")}</div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
